import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import {
  Mail,
  MapPin,
  Send,
  MessageSquare,
  ArrowRight,
  HelpCircle,
  Play,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Puis-je conserver mes ARE tout en me versant des dividendes ?",
    answer: "Oui, c'est possible ! Nous avons accompagné plusieurs freelances dans cette démarche avec France Travail. Isis, par exemple, a économisé +12K€ grâce à cette optimisation. Nous nous occupons de monter le dossier pour vous.",
  },
  {
    question: "Quand créer une holding en plus de ma société d'exploitation ?",
    answer: "La holding devient pertinente quand vous avez du cash à investir (immobilier, financier), que vous souhaitez optimiser la remontée de dividendes, ou que vous lancez d'autres activités. Mehdi a lancé son premier projet immo en SCI moins de 12 mois après sa création grâce à sa holding.",
  },
  {
    question: "Comment fonctionne le passage de micro à société ?",
    answer: "Nous prenons tout en charge : analyse de votre situation, choix de la structure optimale (SAS, SASU, EURL), rédaction des statuts, formalités de création, mise en place de la gestion jusqu'à la radiation de votre ancienne micro. Vous continuez à travailler, on s'occupe du reste.",
  },
  {
    question: "Qu'est-ce que le Club Deal FREELANCEURS ?",
    answer: "C'est un accès exclusif à des opportunités d'investissement (immobilier, startups, projets) réservées à notre communauté de freelances. C'est inclus dans notre offre de diversification pour les business établis.",
  },
  {
    question: "Vous gérez aussi la comptabilité ?",
    answer: "Oui, l'abonnement mensuel inclut la gestion financière complète de votre structure : comptabilité, déclarations TVA, IS, bilan annuel. Tout est géré par notre équipe d'experts et notre partenaire comptables pour que vous n'ayez plus à y penser.",
  },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message envoyé avec succès ! Nous vous répondrons sous 24h.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MessageSquare className="h-4 w-4" />
              Contact
            </div>
            <h1 className="font-manrope font-bold text-4xl md:text-5xl lg:text-6xl text-slate-900 mb-6">
              Parlons de votre{" "}
              <span className="gradient-text">projet freelance</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Une question sur votre statut ? Besoin d'un accompagnement personnalisé ? 
              Notre équipe est là pour vous aider.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="font-manrope font-semibold text-xl text-slate-900 mb-6">
                    Nos coordonnées
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#52fbb0]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-[#52fbb0]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Email</p>
                        <a href="mailto:contact@freelanceurs.io" className="text-slate-600 hover:text-[#52fbb0]">
                          contact@freelanceurs.io
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#52fbb0]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-[#52fbb0]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Adresse</p>
                        <p className="text-slate-600">445 Bd Gambetta</p>
                        <p className="text-slate-600">59200 Tourcoing</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* CTA to Feedbacks */}
              <Card className="border-0 bg-slate-900 text-white">
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Play className="h-5 w-5 text-[#52fbb0]" />
                    <h3 className="font-manrope font-semibold text-xl">
                      Témoignages vidéo
                    </h3>
                  </div>
                  <p className="text-slate-300 mb-6">
                    Découvrez les interviews de nos freelances accompagnés et leurs retours d'expérience.
                  </p>
                  <Link
                    to="/feedbacks"
                    data-testid="feedbacks-link"
                  >
                    <Button className="w-full bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] rounded-full">
                      Voir les interviews
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 md:p-12">
                  <h3 className="font-manrope font-semibold text-2xl text-slate-900 mb-2">
                    Envoyez-nous un message
                  </h3>
                  <p className="text-slate-600 mb-8">
                    Nous vous répondons sous 24 heures ouvrées.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-slate-700 font-medium">
                          Nom complet *
                        </Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          required
                          className="mt-2 h-12 rounded-xl"
                          placeholder="Jean Dupont"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-medium">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          required
                          className="mt-2 h-12 rounded-xl"
                          placeholder="jean@exemple.fr"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-medium">
                          Téléphone
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          className="mt-2 h-12 rounded-xl"
                          placeholder="06 12 34 56 78"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-slate-700 font-medium">
                          Sujet *
                        </Label>
                        <Select
                          value={formData.subject}
                          onValueChange={(value) => handleChange("subject", value)}
                        >
                          <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="contact-subject">
                            <SelectValue placeholder="Sélectionnez un sujet" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="creation">Création d'entreprise</SelectItem>
                            <SelectItem value="optimisation">Optimisation fiscale</SelectItem>
                            <SelectItem value="changement">Changement de statut</SelectItem>
                            <SelectItem value="conseil">Demande de conseil</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-700 font-medium">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange("message", e.target.value)}
                        required
                        className="mt-2 min-h-[150px] rounded-xl"
                        placeholder="Décrivez votre situation et vos besoins..."
                        data-testid="contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-semibold rounded-full py-6 text-lg"
                      data-testid="contact-submit"
                    >
                      {isSubmitting ? (
                        "Envoi en cours..."
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <HelpCircle className="h-4 w-4" />
              FAQ
            </div>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Questions fréquentes
            </h2>
            <p className="text-slate-600 text-lg">
              Retrouvez les réponses aux questions les plus posées par les freelances.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-50 rounded-xl px-6 border-0"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[#52fbb0] py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-slate-600 mb-4">Vous ne trouvez pas la réponse à votre question ?</p>
            <Link to="/simulateur">
              <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] rounded-full px-8">
                Essayer le simulateur
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
